import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { ChartContainer } from '@mui/x-charts/ChartContainer'
import { ChartsAxis } from '@mui/x-charts/ChartsAxis'
import { ChartsGrid } from '@mui/x-charts/ChartsGrid'
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight'
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip'
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart'
import { ScatterPlot } from '@mui/x-charts/ScatterChart'
import {
  BRAND_NAVY,
  BRAND_RED,
  LIGHT_STATUS,
  TOUCH_MIN,
} from 'core/theme.tokens'
import type { GraybookCardData } from './useAssistantChat'

const CURVE_COLORS = [
  LIGHT_STATUS.info.fg,
  BRAND_RED,
  LIGHT_STATUS.success.fg,
  LIGHT_STATUS.purple.fg,
  LIGHT_STATUS.inProgress.fg,
  BRAND_NAVY,
]

const DEAL_COLORS = {
  asking: LIGHT_STATUS.inProgress.fg,
  wholesale: BRAND_NAVY,
  retail: LIGHT_STATUS.success.fg,
  best: LIGHT_STATUS.success.fg,
  poor: LIGHT_STATUS.danger.fg,
}

type ChartMode = 'deal' | 'mileage'

function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `$${Math.round(value).toLocaleString('en-US')}`
}

function formatMiles(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`
  }
  return value.toLocaleString('en-US')
}

function shortLabel(data: GraybookCardData): string {
  return data.vehicle?.stockNumber || data.vehicle?.vin || data.vehicle?.id || 'Truck'
}

function hasCurve(data: GraybookCardData): boolean {
  return Boolean(data.chart?.series?.some(s => s.data.some(v => v != null)))
}

/** Lower is better (asking under wholesale). Missing wholesale sorts last. */
function dealRankScore(card: GraybookCardData): number {
  if (card.dealQuality?.vsWholesalePct != null) return card.dealQuality.vsWholesalePct
  const asking = card.vehicle?.sellerAskingPrice
  const wholesale = card.wholesale
  if (asking != null && wholesale != null && wholesale > 0) {
    return ((asking - wholesale) / wholesale) * 100
  }
  // Fall back to upside vs asking (negate so larger spread ranks better)
  if (asking != null && card.retail != null) {
    return -(card.retail - asking)
  }
  return 999
}

function spreadVsAsking(card: GraybookCardData): number | null {
  const asking = card.vehicle?.sellerAskingPrice
  if (asking == null || card.retail == null) return null
  return card.retail - asking
}

interface GraybookComparisonChartProps {
  cards: GraybookCardData[]
}

/**
 * Multi-truck Gray Book compare:
 * - Deal mode (default): asking vs wholesale (buy) vs retail (sell), ranked best→worst
 * - Mileage mode: formula retail curves with current-odometer markers
 */
export const GraybookComparisonChart: React.FC<GraybookComparisonChartProps> = ({ cards }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState(320)
  const [mode, setMode] = useState<ChartMode>('deal')

  const priced = useMemo(
    () => cards.filter(c => c.retail != null && Number.isFinite(c.retail)),
    [cards],
  )

  const ranked = useMemo(
    () => [...priced].sort((a, b) => dealRankScore(a) - dealRankScore(b)),
    [priced],
  )

  const bestId = ranked[0] ? shortLabel(ranked[0]) : null

  useEffect(() => {
    if (!containerRef.current) return
    const update = () => {
      const width = containerRef.current?.getBoundingClientRect().width || 320
      setChartWidth(Math.max(260, Math.floor(width)))
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const showWholesale = ranked.some(c => c.wholesale != null)
  const labels = ranked.map(shortLabel)
  const askingData = ranked.map(c => c.vehicle?.sellerAskingPrice ?? null)
  const wholesaleData = ranked.map(c => c.wholesale)
  const retailData = ranked.map(c => c.retail)

  const mileageComparable = useMemo(
    () => ranked.filter(c => hasCurve(c) || c.chart?.currentPoint),
    [ranked],
  )

  const mileagePoints = useMemo(() => {
    const withSeries = mileageComparable.find(c => c.chart?.mileagePoints?.length)
    return withSeries?.chart?.mileagePoints ?? [0, 25000, 50000, 75000, 100000, 150000, 200000, 250000]
  }, [mileageComparable])

  const lineSeries = useMemo(() => {
    return mileageComparable.flatMap((card, index) => {
      const color = CURVE_COLORS[index % CURVE_COLORS.length]
      const label = shortLabel(card)
      const formula =
        card.chart?.series?.find(s => s.id === 'formula') ??
        card.chart?.series?.find(s => s.data.some(v => v != null))
      if (!formula) return []
      return [
        {
          type: 'line' as const,
          id: `curve-${index}-${label}`,
          label,
          data: formula.data.map(v => (v == null ? null : v)),
          color,
          showMark: false,
          curve: 'linear' as const,
          connectNulls: true,
          valueFormatter: (value: number | null) => formatMoney(value),
        },
      ]
    })
  }, [mileageComparable])

  const scatterSeries = useMemo(() => {
    return mileageComparable.flatMap((card, index) => {
      const point = card.chart?.currentPoint
      if (!point) return []
      const color = CURVE_COLORS[index % CURVE_COLORS.length]
      const label = shortLabel(card)
      return [
        {
          type: 'scatter' as const,
          id: `now-${index}-${label}`,
          label: `${label} now`,
          data: [{ id: 0, x: point.mileage, y: point.retail }],
          color,
          markerSize: 7,
          valueFormatter: (pt: { x: number | null; y: number | null } | null) => {
            if (!pt) return ''
            return `${formatMiles(pt.x ?? 0)} · ${formatMoney(pt.y)}`
          },
        },
      ]
    })
  }, [mileageComparable])

  if (ranked.length < 2) return null

  const chartHeight = mode === 'deal' ? 260 : 220
  const canShowMileage = lineSeries.length >= 2 || scatterSeries.length >= 2

  return (
    <Box
      ref={containerRef}
      sx={{
        mb: 1.25,
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        gap={1}
        flexWrap='wrap'
        sx={{ mb: 0.75 }}
      >
        <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
          {mode === 'deal'
            ? 'Gray Book · deal compare (best → worst)'
            : 'Gray Book · retail vs mileage'}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={mode}
          onChange={(_, next: ChartMode | null) => {
            if (next) setMode(next)
          }}
          aria-label='Comparison chart mode'
          sx={{
            '& .MuiToggleButton-root': {
              px: 1,
              py: 0.25,
              minHeight: TOUCH_MIN - 12,
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: 600,
            },
          }}
        >
          <ToggleButton value='deal'>Deal</ToggleButton>
          <ToggleButton value='mileage' disabled={!canShowMileage}>
            Mileage
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction='row' flexWrap='wrap' gap={0.5} sx={{ mb: 1 }}>
        {ranked.map((card, index) => {
          const label = shortLabel(card)
          const isBest = index === 0
          const spread = spreadVsAsking(card)
          const quality = card.dealQuality?.label
          const pct = card.dealQuality?.vsWholesalePct
          return (
            <Chip
              key={`${label}-${index}`}
              size='small'
              color={isBest ? 'success' : 'default'}
              variant={isBest ? 'filled' : 'outlined'}
              label={[
                isBest ? `Best · ${label}` : `#${index + 1} ${label}`,
                quality || null,
                pct != null ? `${pct > 0 ? '+' : ''}${pct}% vs WS` : null,
                spread != null ? `spread ${formatMoney(spread)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              sx={{
                height: 22,
                maxWidth: '100%',
                bgcolor: isBest ? undefined : 'action.hover',
                '& .MuiChip-label': {
                  px: 0.75,
                  fontSize: '0.68rem',
                  fontWeight: isBest ? 700 : 600,
                },
              }}
            />
          )
        })}
      </Stack>

      {mode === 'deal' ? (
        <Box sx={{ width: '100%', height: chartHeight }}>
          <BarChart
            width={chartWidth}
            height={chartHeight}
            series={[
              {
                data: askingData,
                label: 'Seller asking',
                color: DEAL_COLORS.asking,
                valueFormatter: (v: number | null) => formatMoney(v),
              },
              ...(showWholesale
                ? [
                    {
                      data: wholesaleData,
                      label: 'Wholesale (buy)',
                      color: DEAL_COLORS.wholesale,
                      valueFormatter: (v: number | null) => formatMoney(v),
                    },
                  ]
                : []),
              {
                data: retailData,
                label: 'Retail (sell)',
                color: DEAL_COLORS.retail,
                valueFormatter: (v: number | null) => formatMoney(v),
              },
            ]}
            xAxis={[
              {
                data: labels,
                scaleType: 'band',
                tickLabelStyle: { fontSize: 10, fontWeight: bestId ? 600 : 500 },
              },
            ]}
            yAxis={[
              {
                valueFormatter: (v: number | null) => (v == null ? '' : formatMoney(v)),
                width: 56,
              },
            ]}
            margin={{ left: 8, right: 8, top: 40, bottom: 28 }}
          />
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: chartHeight }}>
          <ChartContainer
            width={chartWidth}
            height={chartHeight}
            series={[...lineSeries, ...scatterSeries] as never}
            xAxis={[
              {
                data: mileagePoints,
                label: 'Mileage',
                valueFormatter: (v: number | null) => (v == null ? '' : formatMiles(v)),
              },
            ]}
            yAxis={[
              {
                valueFormatter: (v: number | null) => (v == null ? '' : formatMoney(v)),
                width: 56,
              },
            ]}
            margin={{ left: 8, right: 12, top: 12, bottom: 8 }}
          >
            <ChartsGrid horizontal />
            <LinePlot />
            <MarkPlot />
            {scatterSeries.length > 0 ? <ScatterPlot /> : null}
            <ChartsAxis />
            <ChartsAxisHighlight x='line' />
            <ChartsTooltip trigger='axis' />
          </ChartContainer>
        </Box>
      )}

      <Typography variant='caption' color='text.disabled' sx={{ display: 'block', mt: 0.75 }}>
        {mode === 'deal'
          ? bestId
            ? `Best deal left: ${bestId} (asking under wholesale ranks first). Expand a row for reasoning.`
            : 'Expand a row for reasoning.'
          : 'Dots mark each truck’s current miles. Switch to Deal to compare buy/sell.'}
      </Typography>
    </Box>
  )
}
